/**
 * Pipeline tab (Phase 5 Action 49).
 *
 * Recent report submissions for this tenant, freshest first. Answers
 * "what's actually being written?" at a glance. Notes are truncated to
 * a preview server-side (140 chars) so the table stays skimmable; the
 * full text can be pulled later from a dedicated report inspector
 * (Phase 6).
 */

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import { fetchJson, formatRelative, type TenantContext } from './shared';

interface Report {
  id: string;
  createdAt: string;
  authorName: string | null;
  authorEmail: string | null;
  customerName: string | null;
  engagementType: string | null;
  sentiment: 'positive' | 'neutral' | 'negative';
  notesPreview: string;
}

const SENTIMENT_BADGE: Record<Report['sentiment'], string> = {
  positive: 'bg-emerald-100 text-emerald-800',
  neutral:  'bg-slate-100 text-slate-700',
  negative: 'bg-red-100 text-red-800',
};

export default function Pipeline() {
  const { tenant } = useOutletContext<TenantContext>();

  const q = useQuery({
    queryKey: ['tenant-pipeline', tenant.id],
    queryFn: () => fetchJson<{ data: { reports: Report[] } }>(`/api/tenants/${tenant.id}/pipeline`),
  });

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading pipeline…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load pipeline: {q.error instanceof Error ? q.error.message : 'unknown error'}
      </div>
    );
  }

  const reports = q.data.data.reports;

  return (
    <section className="mb-6 rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Recent reports</h2>
        <span className="text-xs text-slate-500">Showing {reports.length} most recent</span>
      </header>
      {reports.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-slate-500">
          No reports submitted yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {reports.map((r) => (
            <li key={r.id} className="px-6 py-4 text-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium shrink-0', SENTIMENT_BADGE[r.sentiment])}>
                    {r.sentiment}
                  </span>
                  <span className="font-medium text-slate-900 truncate">{r.customerName ?? <span className="text-slate-400">unknown customer</span>}</span>
                  {r.engagementType && (
                    <span className="text-xs text-slate-500 shrink-0">· {r.engagementType}</span>
                  )}
                </div>
                <div className="shrink-0 text-xs text-slate-500">{formatRelative(r.createdAt)}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                by {r.authorName ?? r.authorEmail ?? 'unknown'}
              </div>
              {r.notesPreview && (
                <p className="mt-2 text-slate-700 whitespace-pre-wrap">{r.notesPreview}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
